import React, { useState, useEffect } from 'react';
import {
  Shield, Clock, CheckCircle, XCircle, AlertTriangle,
  Eye, ChevronDown, Filter
} from 'lucide-react';
import api from '../services/api';

const statusColors = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pendente', icon: Clock },
  approved: { bg: 'bg-brand-100', text: 'text-brand-700', label: 'Aprovado', icon: CheckCircle },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejeitado', icon: XCircle },
  completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Concluido', icon: CheckCircle }
};

const typeLabels = {
  correction: 'Correcao',
  deletion: 'Eliminacao',
  access: 'Acesso',
  portability: 'Portabilidade'
};

const DataCorrectionAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadRequests();
  }, [filterStatus]);

  const loadRequests = async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/lgpd/correction-requests', { params });
      setRequests(res.data.data);
    } catch (err) {
      setError('Erro ao carregar solicitacoes.');
      console.error('Error loading requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (id, status) => {
    setProcessing(true);
    setError(null);
    try {
      await api.put(`/lgpd/correction-requests/${id}/review`, {
        status,
        reviewNotes
      });
      setSuccess(`Solicitacao ${status === 'approved' ? 'aprovada' : 'rejeitada'} com sucesso.`);
      setSelectedRequest(null);
      setReviewNotes('');
      await loadRequests();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao processar solicitacao.');
    } finally {
      setProcessing(false);
    }
  };

  const handleComplete = async (id) => {
    setProcessing(true);
    setError(null);
    try {
      await api.put(`/lgpd/correction-requests/${id}/complete`);
      setSuccess('Solicitacao concluida com sucesso.');
      setSelectedRequest(null);
      await loadRequests();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao concluir solicitacao.');
    } finally {
      setProcessing(false);
    }
  };

  const getSLAStatus = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const days = Math.floor((now - created) / (1000 * 60 * 60 * 24));
    if (days > 15) return { label: `${days} dias (ATRASADO)`, color: 'text-red-600' };
    if (days > 10) return { label: `${days} dias`, color: 'text-yellow-600' };
    return { label: `${days} dias`, color: 'text-gray-500' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 rounded-lg">
            <Shield className="h-6 w-6 text-brand-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Solicitacoes LGPD</h1>
            <p className="text-sm text-gray-600">
              Gerenciar solicitacoes de dados dos titulares
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border-gray-300 rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="">Todos os status</option>
            <option value="pending">Pendente</option>
            <option value="approved">Aprovado</option>
            <option value="rejected">Rejeitado</option>
            <option value="completed">Concluido</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {success}
        </div>
      )}

      {/* Requests Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Solicitante</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SLA</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acoes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {requests.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-sm text-gray-500 text-center">
                  Nenhuma solicitacao encontrada.
                </td>
              </tr>
            ) : (
              requests.map(request => {
                const statusConfig = statusColors[request.status] || statusColors.pending;
                const StatusIcon = statusConfig.icon;
                const sla = getSLAStatus(request.createdAt);

                return (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {request.requester?.firstName} {request.requester?.lastName}
                      </div>
                      <div className="text-xs text-gray-500">{request.requester?.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900">{typeLabels[request.requestType] || request.requestType}</span>
                      {request.fieldName && (
                        <div className="text-xs text-gray-500">{request.fieldName}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs ${sla.color}`}>{sla.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">
                        {new Date(request.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedRequest(request)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-brand-600 hover:bg-brand-50 rounded"
                      >
                        <Eye className="h-3 w-3" />
                        Ver
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Detalhes da Solicitacao</h3>
                <button onClick={() => { setSelectedRequest(null); setReviewNotes(''); }} className="text-gray-400 hover:text-gray-600">
                  &times;
                </button>
              </div>

              <div className="space-y-3 mb-6">
                <div>
                  <span className="text-xs font-medium text-gray-500">Solicitante:</span>
                  <p className="text-sm text-gray-900">{selectedRequest.requester?.firstName} {selectedRequest.requester?.lastName} ({selectedRequest.requester?.email})</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500">Tipo:</span>
                  <p className="text-sm text-gray-900">{typeLabels[selectedRequest.requestType]}</p>
                </div>
                {selectedRequest.fieldName && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Campo:</span>
                    <p className="text-sm text-gray-900">{selectedRequest.fieldName}</p>
                  </div>
                )}
                {selectedRequest.currentValue && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Valor Atual:</span>
                    <p className="text-sm text-gray-900">{selectedRequest.currentValue}</p>
                  </div>
                )}
                {selectedRequest.requestedValue && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Valor Solicitado:</span>
                    <p className="text-sm text-gray-900">{selectedRequest.requestedValue}</p>
                  </div>
                )}
                <div>
                  <span className="text-xs font-medium text-gray-500">Motivo:</span>
                  <p className="text-sm text-gray-900">{selectedRequest.reason}</p>
                </div>
                {selectedRequest.reviewNotes && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Notas do Revisor:</span>
                    <p className="text-sm text-gray-900">{selectedRequest.reviewNotes}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {selectedRequest.status === 'pending' && (
                <div className="border-t pt-4 space-y-3">
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={2}
                    placeholder="Notas de revisao (opcional)"
                    className="w-full border-gray-300 rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleReview(selectedRequest.id, 'approved')}
                      disabled={processing}
                      className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                    >
                      Aprovar
                    </button>
                    <button
                      onClick={() => handleReview(selectedRequest.id, 'rejected')}
                      disabled={processing}
                      className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                    >
                      Rejeitar
                    </button>
                  </div>
                </div>
              )}

              {selectedRequest.status === 'approved' && (
                <div className="border-t pt-4">
                  <button
                    onClick={() => handleComplete(selectedRequest.id)}
                    disabled={processing}
                    className="w-full px-3 py-2 bg-brand-700 text-white rounded-lg text-sm font-medium hover:bg-brand-800 disabled:opacity-50"
                  >
                    Marcar como Concluido
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataCorrectionAdmin;
